import { Suspense, useEffect, useMemo } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import { useSuperAdminPanel } from "./SuperAdminPanelProvider.tsx";
import { SuperAdminNavBadge } from "./SuperAdminNavBadge.tsx";
import styles from "./SuperAdminPanel.module.scss";

export function SuperAdminPanel() {
  const { hasToolAccess, tools, isOpen, activeToolId, closePanel, switchTool } = useSuperAdminPanel();

  const activeTool = useMemo(
    () => tools.find((t) => t.id === activeToolId) ?? tools[0],
    [activeToolId, tools],
  );

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closePanel();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closePanel]);

  if (!hasToolAccess || !isOpen || !activeTool) return null;

  const ActiveComponent = activeTool.component;

  return (
    <>
      <div className={styles.overlay} onClick={closePanel} />
      <aside className={styles.panel} role="dialog" aria-modal="true" aria-label="SuperAdmin Panel">
        <div className={styles.header}>
          <SuperAdminNavBadge onPick={closePanel} />
          <select
            className={styles.toolSelect}
            value={activeTool.id}
            onChange={(e) => switchTool(e.target.value)}
          >
            {tools.map((t) => (
              <option key={t.id} value={t.id}>
                {t.shortcutKey ? `${t.name}  ·  Ctrl+Shift+Alt+${t.shortcutKey}` : t.name}
              </option>
            ))}
          </select>
          <button className={styles.closeBtn} onClick={closePanel} aria-label="Close">
            &times;
          </button>
        </div>
        <div className={styles.body}>
          <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
            <ActiveComponent />
          </Suspense>
        </div>
      </aside>
    </>
  );
}

export function SuperAdminToggle() {
  const { hasToolAccess, openPanel, saHidden, setSaHidden } = useSuperAdminPanel();
  const { impersonation } = useAuth();
  if (!hasToolAccess) return null;

  const isImpersonating = impersonation != null;

  if (saHidden) {
    // Only show the restore button when impersonating (otherwise nothing to hide)
    if (!isImpersonating) return null;
    return (
      <button
        onClick={() => setSaHidden(false)}
        className={styles.toggleBtn}
        title="Show SuperAdmin UI"
        aria-label="Show SuperAdmin UI"
        style={{ opacity: 0.3, fontSize: "10px" }}
      >
        SA
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => openPanel()}
        className={styles.toggleBtn}
        title="SuperAdmin Panel (Ctrl+Shift+Alt+Q)"
        aria-label="Open SuperAdmin Panel"
      >
        SA
      </button>
      {isImpersonating && (
      <button
        onClick={() => setSaHidden(true)}
        className={styles.toggleBtn}
        title="Hide SuperAdmin UI (for screenshots)"
        aria-label="Hide SuperAdmin UI"
        style={{ opacity: 0.5, fontSize: "10px", padding: "2px 4px" }}
      >
        &times;
      </button>
      )}
    </>
  );
}
